import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'services/hooks/use-unsaved-changes-warning/useBlocker';

type BlockedLocation = string | { pathname?: string; search?: string } | undefined;

// Where a blocked push was headed, as a pathname and its query.
export const parseBlockedLocation = (location: BlockedLocation) => {
  if (typeof location === 'string') {
    const [pathname, search = ''] = location.split('?');
    return { pathname: pathname || window.location.pathname, search: new URLSearchParams(search) };
  }
  return {
    pathname: location?.pathname || window.location.pathname,
    search: new URLSearchParams(location?.search ?? ''),
  };
};

// Prompts before leaving with unsaved edits. allowNextNavigation lets one move through
// right after a save, since the dirty flag that armed the guard clears a render later.
export const useLeaveGuard = (active: boolean, shouldAllow?: (location: BlockedLocation) => boolean) => {
  const bypassRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [pendingRetry, setPendingRetry] = useState<(() => void) | null>(null);
  // useBlocker's retry disarms it for good; a new blocker identity re-installs it.
  const [armKey, setArmKey] = useState(0);

  useEffect(() => {
    if (!active) {
      bypassRef.current = false;
    }
  }, [active]);

  const blocker = useCallback(
    (tx: { location: BlockedLocation; retry: () => void }) => {
      // !activeRef: the guard turned off this render but useBlocker's cleanup hasn't run yet.
      if (bypassRef.current || !activeRef.current || shouldAllow?.(tx.location)) {
        bypassRef.current = false;
        tx.retry();
        setArmKey(key => key + 1);
        return;
      }
      setPendingRetry(() => tx.retry);
    },
    // armKey only re-creates the blocker so useBlocker re-arms after a let-through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shouldAllow, armKey]
  );

  useBlocker(blocker, active);

  // Only while armed: once the guard is off the move needs no bypass, and a leftover
  // flag would wave through the next leave after fresh edits.
  const allowNextNavigation = useCallback(() => {
    bypassRef.current = activeRef.current;
  }, []);

  const confirmLeave = useCallback(() => {
    const retry = pendingRetry;
    setPendingRetry(null);
    retry?.();
  }, [pendingRetry]);

  const cancelLeave = useCallback(() => setPendingRetry(null), []);

  return { isLeavePending: pendingRetry !== null, confirmLeave, cancelLeave, allowNextNavigation };
};
